/**
 * What each role may do.
 *
 * This replaced a single question — "does this person work for CFOAI?" — which
 * was asked at every guarded endpoint. That question had two answers and the
 * product has four roles, so two of them meant nothing: a client_admin could
 * do no more than a client_user, and a cfoai_staff no less than a cfoai_admin.
 *
 * Roles are still held per company. What changed is that a client's own
 * administrator can now run their own books — create contacts and items, file
 * documents, record payments — while the things that belong to the practice
 * rather than the client stay with the practice.
 *
 * WHY CAPABILITIES RATHER THAN ROLE COMPARISONS
 * An endpoint that asks "is this person CFOAI?" has decided a policy question
 * in the wrong place. An endpoint that asks "may this person file documents?"
 * states what it needs, and the answer lives here, once, where it can be read
 * in full and tested.
 */

export type Role = 'client_user' | 'client_admin' | 'cfoai_staff' | 'cfoai_admin';

export const ROLES: readonly Role[] = ['client_user', 'client_admin', 'cfoai_staff', 'cfoai_admin'];

/**
 * Capabilities, from least to most.
 *
 * - read       Everything this company holds: lists, documents, the dashboard,
 *              and the branding a printed document needs.
 * - submit     Prepare a document and put it in the queue for review. Not
 *              filing it — preparing is not the same act, and a client_user
 *              exists precisely to do the first without the second.
 * - operate    Run the books: file documents to Xero, record payments, bring
 *              in contacts, set how documents look and how the company is paid.
 * - administer The practice's own controls: binding a client's books to this
 *              product, granting and revoking access, and the record of
 *              everything ever filed.
 */
export type Capability = 'read' | 'submit' | 'operate' | 'administer';

/**
 * Deliberately a ladder rather than a set per role.
 *
 * Every role that can file a document can also prepare one, and every role
 * that can grant access can also file. A table of independent flags would
 * allow a role that administers but cannot read — which is not a policy
 * anybody wants, only one somebody could enter by mistake.
 */
const LADDER: readonly Capability[] = ['read', 'submit', 'operate', 'administer'];

const REACH: Record<Role, Capability> = {
  client_user: 'submit',
  client_admin: 'operate',
  cfoai_staff: 'administer',
  cfoai_admin: 'administer',
};

export function can(role: Role, capability: Capability): boolean {
  return LADDER.indexOf(REACH[role]) >= LADDER.indexOf(capability);
}

/**
 * Which of two roles on the same company wins.
 *
 * Firm-wide CFOAI membership and a per-company entitlement can both name the
 * same person. Rather than declare one source authoritative — and have
 * somebody quietly lose access because a grant was made in the other place —
 * the stronger of the two applies.
 */
export function strongest(a: Role, b: Role): Role {
  return ROLES.indexOf(a) >= ROLES.indexOf(b) ? a : b;
}

/** Whether this role belongs to the practice rather than to the client. */
export function isFirmRole(role: Role): boolean {
  return role === 'cfoai_staff' || role === 'cfoai_admin';
}
